import classNames from 'classnames'
import { UseSelectReturnValue, useSelect } from 'downshift'
import React, { useContext, useRef, useState } from 'react'

import { LocaleContext } from '../locale/i18n'
import { normalizeMatchString } from '../logic/productFiltering'

import Button, { buttonClasses } from './Button'
import IconCaret from './icons/IconCaret'
import IconClear from './icons/IconClear'

export interface Option<T> {
  label: string
  value: T
}

export interface GroupedOption<T> {
  heading: string
  options: Option<T>[]
}

export interface SelectProps<T> {
  onChange: (newValue: Option<T> | null) => void
  options: (Option<T> | GroupedOption<T>)[]
  renderOption?: (option: Option<T>) => React.ReactNode
  value?: Option<T> | null
  placeholder: string
  searchPlaceholder?: string
  noOptionsMessage?: string
  variant?: 'button' | 'select'
  ['data-test-id']?: string
}

export function isGroupedOption<T>(
  option: Option<T> | GroupedOption<T>,
): option is GroupedOption<T> {
  return (
    'heading' in option && 'options' in option && Array.isArray(option.options)
  )
}

const SelectItem = <T,>({
  option,
  ...props
}: {
  option: Option<T> | GroupedOption<T>
  renderOption: (option: Option<T>) => React.ReactNode
  getIndex: (option: Option<T>) => number
} & Pick<
  UseSelectReturnValue<Option<T>>,
  'getItemProps' | 'highlightedIndex'
>) => {
  const isGrouped = isGroupedOption(option)

  if (isGrouped) {
    return (
      <li>
        <div className="flex w-max min-w-full cursor-default items-center gap-2 px-2 py-2 text-left text-sm font-bold">
          {option.heading}
        </div>
        <ul>
          {option.options.map((item, index) => {
            return <SelectItem option={item} key={index} {...props} />
          })}
        </ul>
      </li>
    )
  }

  const index = props.getIndex(option)
  const { onClick, ...itemProps } = props.getItemProps({ index, item: option })
  return (
    <li
      className={classNames(
        'flex min-w-full cursor-pointer items-center gap-2 px-2 py-3 text-left hover:bg-secondary-light2',
        { 'bg-secondary-light2': props.highlightedIndex === index },
      )}
      {...itemProps}
      onClick={e => {
        if (onClick) {
          e.stopPropagation()
          onClick(e)
        }
      }}
    >
      {props.renderOption(option)}
    </li>
  )
}

const Select = <T,>({
  'data-test-id': dataTestId,
  onChange,
  options,
  renderOption = option => option.label,
  value,
  noOptionsMessage,
  placeholder,
  searchPlaceholder,
  variant = 'select',
}: SelectProps<T>) => {
  const [searchInput, setSearchInput] = useState('')
  const { t } = useContext(LocaleContext)

  const normalizedQuery = normalizeMatchString(searchInput)
  const filteredOptions = options
    .map(option =>
      isGroupedOption(option)
        ? {
            ...option,
            options: normalizeMatchString(option.heading).includes(
              normalizedQuery,
            )
              ? option.options
              : option.options.filter(({ label }) =>
                  normalizeMatchString(label).includes(normalizedQuery),
                ),
          }
        : option,
    )
    .filter(option =>
      isGroupedOption(option)
        ? option.options.length > 0
        : normalizeMatchString(option.label).includes(normalizedQuery),
    )

  const flatOptions: Option<T>[] = filteredOptions.flatMap(option =>
    isGroupedOption(option) ? option.options : option,
  )

  const containerEl = useRef<HTMLDivElement>(null)
  const inputEl = useRef<HTMLInputElement>(null)
  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
    closeMenu,
  } = useSelect({
    defaultHighlightedIndex: 0,
    itemToString: item => (item ? item.label : ''),
    items: flatOptions,
    onIsOpenChange: s => {
      if (s.isOpen) {
        inputEl.current?.focus()
      }
    },
    onSelectedItemChange: ({ selectedItem }) => {
      onChange(selectedItem ?? null)
      setSearchInput('')
    },
    selectedItem: flatOptions.find(o => o.value === value),
    stateReducer: (state, actionAndChanges) => {
      const { type, changes } = actionAndChanges

      if (type === useSelect.stateChangeTypes.ToggleButtonBlur) {
        return { selectedItem: state.selectedItem }
      }
      return changes
    },
  })

  const {
    onBlur: _, // handled by parent
    ...toggleButtonProps
  } = getToggleButtonProps()

  return (
    <div
      onBlur={e => {
        if (containerEl.current?.contains(e.relatedTarget)) {
          return
        }
        setSearchInput('')
        closeMenu()
      }}
      className="relative text-base"
      ref={containerEl}
    >
      <label className="hidden" {...getLabelProps()}>
        {placeholder}
      </label>
      <div
        data-test-id={dataTestId}
        className={classNames(
          'whitespace-nowrap select-none',
          variant === 'select' && [
            'pointer flex w-full items-center justify-center rounded-full border text-text outline-transparent',
            value
              ? 'border-[#f7c300] bg-[#efe4c8]'
              : 'border-[#c2d6ff] bg-primary-light',
          ],
          variant === 'button' && [buttonClasses(), 'px-0!'],
        )}
      >
        <div
          {...toggleButtonProps}
          className={classNames(
            'flex w-full items-center justify-center',
            variant === 'select' && [
              'gap-1 rounded-full px-2.5 py-1.25 focus-visible:shadow-glow focus-visible:outline',
              value
                ? 'text-sm focus-visible:shadow-[#f7c300]/25 focus-visible:outline-[#d8a200]'
                : 'focus-visible:shadow-primary/25 focus-visible:outline-primary',
            ],
            variant === 'button' && 'py-px pr-0.5 pl-3',
          )}
        >
          <span className="flex w-full items-center gap-2 overflow-hidden text-left overflow-ellipsis">
            {value ? renderOption(value) : placeholder}
          </span>
          {value ? (
            <Button
              variant="plain"
              aria-label="clear selection"
              className="p-1"
              onClick={e => {
                e.stopPropagation()
                onChange(null)
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.stopPropagation()
                  onChange(null)
                }
              }}
            >
              <IconClear stroke="#1a2b49" />
            </Button>
          ) : (
            <IconCaret className="m-2" width="10" height="10" open={isOpen} />
          )}
        </div>
      </div>
      <div
        className={classNames(
          'absolute z-10 flex w-max max-w-64 min-w-full flex-col rounded-lg border border-gray-300 bg-white shadow-md sm:max-w-128',
          isOpen ? null : 'hidden',
        )}
        {...getMenuProps()}
      >
        <input
          className="inputField inputField--small m-2 rounded-lg border px-1 py-2"
          type="search"
          placeholder={searchPlaceholder || placeholder}
          onChange={e => setSearchInput(e.target.value)}
          // propagate keydown for e.g. selecting items
          onKeyDown={e => {
            if (
              [
                'ArrowUp',
                'ArrowDown',
                'Home',
                'End',
                'Escape',
                'Enter',
                'PageUp',
                'PageDown',
              ].includes(e.key)
            ) {
              toggleButtonProps.onKeyDown?.(e)
            }
          }}
          value={searchInput}
          ref={inputEl}
        />
        {isOpen && (
          <ul className="max-h-60 overflow-y-auto" tabIndex={-1}>
            {flatOptions.length === 0 && (
              <li className="text text--subtle w-full px-5 py-2 text-left text-sm">
                {noOptionsMessage || t('productFilters.noOptions')}
              </li>
            )}

            {filteredOptions.map((option, index) => (
              <SelectItem
                getItemProps={getItemProps}
                highlightedIndex={highlightedIndex}
                key={index}
                option={option}
                renderOption={renderOption}
                getIndex={item =>
                  flatOptions.findIndex(i => i.value === item.value)
                }
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

export default Select
